import { useEffect, useMemo, useState } from 'react';
import { translations } from '../data/translations';
import { AppContext } from './AppContext';

const supportedLanguages = ['en', 'de', 'fa'];

function getInitialLanguage() {
  const routeLanguage = window.location.pathname.split('/')[1];
  return supportedLanguages.includes(routeLanguage) ? routeLanguage : 'en';
}

function getInitialTheme() {
  const savedTheme = window.localStorage.getItem('az-theme');
  if (savedTheme === 'dark' || savedTheme === 'light') return savedTheme;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

// The provider synchronises URL, document direction and persisted theme state.
export function AppProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = language;
    root.dir = language === 'fa' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#000000' : '#ffffff');
    window.localStorage.setItem('az-theme', theme);
  }, [theme]);

  function setLanguage(nextLanguage) {
    if (!supportedLanguages.includes(nextLanguage)) return;
    const nextPath = nextLanguage === 'en' ? '/' : `/${nextLanguage}`;
    window.history.pushState({}, '', `${nextPath}${window.location.hash}`);
    setLanguageState(nextLanguage);
  }

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      theme,
      toggleTheme: () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
      t: translations[language],
    }),
    [language, theme],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
